import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMemo, useCallback, ReactElement } from 'react';
import { useSelector } from 'react-redux';
import { ErrorBoundary } from 'react-error-boundary';
import { FeatureCardProps } from '../../components/common/data/cards/FeatureCard';
import { DashboardView } from './DashboardView';
import { ErrorFallback } from '../../components/common/core/ErrorFallback';
import { logger } from '@utils/logging-utils';
import { RootState } from '@state/root-state';
import { selectCurrentOrganizationId, hasPermission } from '@state/permissions-slice';
import { Permissions } from '@auth/permissions';
import { managementApi } from '@api/management/management-api';

// Import Material-UI icons
import DomainOutlined from '@mui-symbols-material/w400/DomainOutlined';
import HandshakeOutlined from '@mui-symbols-material/w400/HandshakeOutlined';
import PinOutlined from '@mui-symbols-material/w400/PinOutlined';
import ArticleOutlined from '@mui-symbols-material/w400/ArticleOutlined';
import WavingHandOutlined from '@mui-symbols-material/w400/WavingHandOutlined';
import WorkOutlined from '@mui-symbols-material/w400/WorkOutlined';
import SquareFootOutlined from '@mui-symbols-material/w400/SquareFootOutlined';
import AddCardOutlined from '@mui-symbols-material/w400/AddCardOutlined';
import ImageOutlined from '@mui-symbols-material/w400/ImageOutlined';
interface CardConfig {
  readonly icon: FeatureCardProps['icon'];
  readonly route: string;
  readonly featureName: string;
  readonly testId: string;
  readonly titleKey: string;
  readonly subtitleKey: string;
  readonly permissionId: string;
  readonly isPrimary: boolean;
}

/**
 * Consolidated card configuration for all dashboard features
 */
const CARD_CONFIGS: ReadonlyArray<CardConfig> = [
  // Primary cards - main features
  {
    icon: DomainOutlined,
    route: '/organizations',
    featureName: 'Organizations',
    testId: 'organizations-card',
    titleKey: 'layouts:dashboard.organizations.title',
    subtitleKey: 'layouts:dashboard.organizations.subtitle',
    permissionId: Permissions.OrganizationRead,
    isPrimary: true
  },
  {
    icon: HandshakeOutlined,
    route: '/business-partners',
    featureName: 'Business Partners',
    testId: 'business-partners-card',
    titleKey: 'layouts:dashboard.businessPartners.title',
    subtitleKey: 'layouts:dashboard.businessPartners.subtitle',
    permissionId: Permissions.BusinessPartnersRead,
    isPrimary: true
  },
  // Secondary cards
  {
    icon: PinOutlined,
    route: '/number-ranges',
    featureName: 'Number Ranges',
    testId: 'number-ranges-card',
    titleKey: 'layouts:dashboard.numberRanges.title',
    subtitleKey: 'layouts:dashboard.numberRanges.subtitle',
    permissionId: Permissions.NumberRangesRead,
    isPrimary: false
  },
  {
    icon: ArticleOutlined,
    route: '/texts',
    featureName: 'Texts',
    testId: 'texts-card',
    titleKey: 'layouts:dashboard.texts.title',
    subtitleKey: 'layouts:dashboard.texts.subtitle',
    permissionId: Permissions.TextsRead,
    isPrimary: false
  },
  {
    icon: WavingHandOutlined,
    route: '/salutations',
    featureName: 'Salutations',
    testId: 'salutations-card',
    titleKey: 'layouts:dashboard.salutations.title',
    subtitleKey: 'layouts:dashboard.salutations.subtitle',
    permissionId: Permissions.SalutationsRead,
    isPrimary: false
  },
  {
    icon: WorkOutlined,
    route: '/functions',
    featureName: 'Functions',
    testId: 'functions-card',
    titleKey: 'layouts:dashboard.functions.title',
    subtitleKey: 'layouts:dashboard.functions.subtitle',
    permissionId: Permissions.FunctionsRead,
    isPrimary: false
  },
  {
    icon: SquareFootOutlined,
    route: '/quantity-units',
    featureName: 'Quantity Units',
    testId: 'quantity-units-card',
    titleKey: 'layouts:dashboard.quantityUnits.title',
    subtitleKey: 'layouts:dashboard.quantityUnits.subtitle',
    permissionId: Permissions.QuantityUnitsRead,
    isPrimary: false
  },
  {
    icon: AddCardOutlined,
    route: '/free-fields',
    featureName: 'Free Fields',
    testId: 'free-fields-card',
    titleKey: 'layouts:dashboard.freeFields.title',
    subtitleKey: 'layouts:dashboard.freeFields.subtitle',
    permissionId: Permissions.CustomFieldsRead,
    isPrimary: false
  },
  {
    icon: ImageOutlined,
    route: '/report-images',
    featureName: 'Report Images',
    testId: 'report-images-card',
    titleKey: 'layouts:dashboard.reportImages.title',
    subtitleKey: 'layouts:dashboard.reportImages.subtitle',
    permissionId: Permissions.ReportImagesRead,
    isPrimary: false
  }
] as const;

/**
 * Dashboard wrapper component that handles logic and context dependencies.
 *
 * @component
 * @category Pages
 * @group Dashboard
 */
export function Dashboard(): ReactElement {
  const { t } = useTranslation(['layouts']);
  const navigate = useNavigate();
  const organizationId = useSelector(selectCurrentOrganizationId);
  const permissionsState = useSelector((state: RootState) => state.permissions);

  // Check if permissions are still loading
  const isPermissionsLoading = useSelector((state: RootState) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const managementApiState = (state as any)[managementApi.reducerPath];
    const queries = managementApiState?.queries ?? {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return Object.values(queries).some((query: any) => query?.endpointName === 'fetchPermissions' && query?.status === 'pending');
  });

  // Determine if dashboard should show loading state
  const isDashboardLoading = useMemo(() => {
    // Show loading if permissions are being fetched
    if (isPermissionsLoading) {
      return true;
    }

    // Show loading if we don't have organization ID yet (waiting for permissions to load)
    if (!organizationId && !permissionsState.userPermission) {
      return true;
    }

    return false;
  }, [isPermissionsLoading, organizationId, permissionsState.userPermission]);

  /**
   * Get all authorized cards with permission filtering
   */
  const authorizedCards = useMemo((): ReadonlyArray<FeatureCardProps> => {
    if (!organizationId) {
      return [];
    }

    return CARD_CONFIGS.filter(config => hasPermission({ permissions: permissionsState } as RootState, config.permissionId)).map(config => ({
      icon: config.icon,
      title: t(config.titleKey),
      subtitle: t(config.subtitleKey),
      onClick: () => {
        logger.info('Dashboard', `Navigating to ${config.featureName}`, {
          route: config.route,
          organizationId
        });
        navigate(config.route);
      },
      testId: config.testId,
      'aria-label': `Navigate to ${config.featureName}`,
      permissionId: config.permissionId,
      isPrimary: config.isPrimary
    }));
  }, [organizationId, t, navigate, permissionsState]);

  /**
   * Separate cards into primary and secondary based on configuration
   */
  const primaryCards = useMemo(() => {
    return authorizedCards.filter(card => card.isPrimary === true);
  }, [authorizedCards]);

  const secondaryCards = useMemo(() => {
    return authorizedCards.filter(card => card.isPrimary !== true);
  }, [authorizedCards]);

  const title = t('breadCrumbs.dashboard.page');

  /**
   * Error boundary handler for logging dashboard errors
   */
  const handleError = useCallback(
    (error: Error, errorInfo: React.ErrorInfo) => {
      logger.error('Dashboard', 'Error caught by dashboard error boundary', {
        errorMessage: error.message,
        errorName: error.name,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        organizationId,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: globalThis.location.href
      });
    },
    [organizationId]
  );

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      <DashboardView
        title={title}
        primaryCards={primaryCards}
        secondaryCards={secondaryCards}
        isLoading={isDashboardLoading}
        navigationError={null}
      />
    </ErrorBoundary>
  );
}
